/**
 * Local session registry
 *
 * Tracks sessions in ~/.spacecode/sessions.json so the CLI
 * can list, attach by name, and clean up without depending on the backend.
 */

import { readFile, writeFile, mkdir, rename } from 'fs/promises';
import { join } from 'path';
import { getConfigDir } from './config.js';

export interface SessionRecord {
  /** Session ID (8-char hex from the worker). */
  id: string;
  /** User-assigned name (optional). */
  name?: string;
  /** Repository URL cloned into the sandbox. */
  repo?: string;
  /** Last known status. */
  status: 'running' | 'stopped' | 'destroyed';
  /** ISO timestamp when the session was created. */
  createdAt: string;
  /** Idle timeout that was configured (minutes). */
  idleTimeoutMinutes?: number;
}

const SESSIONS_FILE = 'sessions.json';

function sessionsPath(): string {
  return join(getConfigDir(), SESSIONS_FILE);
}

/**
 * Load all sessions from disk.
 */
export async function loadSessions(): Promise<SessionRecord[]> {
  try {
    const data = await readFile(sessionsPath(), 'utf-8');
    const parsed = JSON.parse(data);
    if (!Array.isArray(parsed)) return [];
    return parsed as SessionRecord[];
  } catch (err) {
    if ((err as NodeJS.ErrnoException).code === 'ENOENT') return [];
    console.warn(`Failed to load sessions.json: ${err instanceof Error ? err.message : String(err)}`);
    return [];
  }
}

/**
 * Save sessions to disk (atomic write).
 */
async function saveSessions(sessions: SessionRecord[]): Promise<void> {
  const dir = getConfigDir();
  await mkdir(dir, { recursive: true, mode: 0o700 });
  const filePath = sessionsPath();
  const tmpPath = `${filePath}.${process.pid}.tmp`;
  await writeFile(tmpPath, JSON.stringify(sessions, null, 2));
  await rename(tmpPath, filePath);
}

/**
 * Add or update a session in the registry.
 */
export async function saveSession(session: SessionRecord): Promise<void> {
  const sessions = await loadSessions();
  const idx = sessions.findIndex(s => s.id === session.id);
  if (idx >= 0) {
    sessions[idx] = session;
  } else {
    sessions.push(session);
  }
  await saveSessions(sessions);
}

/**
 * Remove a session by ID.
 */
export async function removeSession(id: string): Promise<void> {
  const sessions = await loadSessions();
  const filtered = sessions.filter(s => s.id !== id);
  if (filtered.length !== sessions.length) {
    await saveSessions(filtered);
  }
}

/**
 * Mark a session as destroyed.
 */
export async function markSessionDestroyed(id: string): Promise<void> {
  const sessions = await loadSessions();
  const session = sessions.find(s => s.id === id);
  if (session) {
    session.status = 'destroyed';
    await saveSessions(sessions);
  }
}

/**
 * Find a session by name (case-insensitive).
 */
export function findSessionByName(sessions: SessionRecord[], name: string): SessionRecord | undefined {
  return sessions.find(s => s.name?.toLowerCase() === name.toLowerCase());
}

/**
 * Find a session by ID or name. Returns undefined if not found.
 */
export function resolveSession(sessions: SessionRecord[], idOrName: string): SessionRecord | undefined {
  // Try exact ID match first
  const byId = sessions.find(s => s.id === idOrName);
  if (byId) return byId;

  // Try name match
  return findSessionByName(sessions, idOrName);
}

/**
 * Check if a name is already taken by a non-destroyed session.
 */
export function isNameTaken(sessions: SessionRecord[], name: string): SessionRecord | undefined {
  return sessions.find(
    s => s.name?.toLowerCase() === name.toLowerCase() && s.status !== 'destroyed',
  );
}

/**
 * Prune destroyed sessions older than the given number of hours (default: 24).
 */
export async function pruneSessions(olderThanHours = 24): Promise<number> {
  const sessions = await loadSessions();
  const cutoff = Date.now() - olderThanHours * 60 * 60 * 1000;
  const kept = sessions.filter(
    s => s.status !== 'destroyed' || new Date(s.createdAt).getTime() > cutoff,
  );
  const pruned = sessions.length - kept.length;
  if (pruned > 0) {
    await saveSessions(kept);
  }
  return pruned;
}

/**
 * Get only active (non-destroyed) sessions.
 */
export function activeSessions(sessions: SessionRecord[]): SessionRecord[] {
  return sessions.filter(s => s.status !== 'destroyed');
}
